import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const STATUS_CONFIG = {
  present: { label:'Present', icon:'✅', bg:'bg-green-100',  text:'text-green-700',  ring:'ring-green-400'  },
  late:    { label:'Late',    icon:'⏰', bg:'bg-yellow-100', text:'text-yellow-700', ring:'ring-yellow-400' },
  absent:  { label:'Absent',  icon:'❌', bg:'bg-red-100',    text:'text-brand-600',  ring:'ring-brand-400'  },
  excused: { label:'Excused', icon:'🏥', bg:'bg-blue-100',   text:'text-blue-700',   ring:'ring-blue-400'   },
};

export default function TeacherAttendancePage() {
  const { addToast } = useToast();
  const [cohorts, setCohorts]   = useState([]);
  const [selected, setSelected] = useState('');
  const [date, setDate]         = useState(new Date().toISOString().split('T')[0]);
  const [records, setRecords]   = useState([]);   // [{ student_id, name, status, notes }]
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    API.get('/cohorts').then(({ data }) => setCohorts(data.cohorts || [])).catch(() => {});
  }, []);

  const load = () => {
    if (!selected || !date) return;
    setLoading(true);
    API.get(`/attendance/cohort/${selected}/date/${date}`)
      .then(({ data }) => setRecords(data.attendance || []))
      .catch(() => addToast('Failed to load attendance', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [selected, date]);

  const setStatus = (studentId, status) => {
    setRecords(p => p.map(r => r.student_id === studentId ? { ...r, status } : r));
  };

  const markAll = (status) => setRecords(p => p.map(r => ({ ...r, status })));

  const handleSave = async () => {
    if (!selected || !date) { addToast('Select cohort and date', 'error'); return; }
    setSaving(true);
    try {
      await API.post('/attendance/mark', {
        cohortId: parseInt(selected),
        date,
        records: records.map(r => ({ studentId: r.student_id, status: r.status, notes: r.notes || '' })),
      });
      addToast('Attendance saved!', 'success');
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const summary = {
    present: records.filter(r => r.status === 'present').length,
    late:    records.filter(r => r.status === 'late').length,
    absent:  records.filter(r => r.status === 'absent').length,
    excused: records.filter(r => r.status === 'excused').length,
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <SectionHeader title="Attendance" subtitle="Mark student attendance for each class"/>

      {/* Controls */}
      <Card className="p-4">
        <div className="flex gap-4 flex-wrap items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-bold text-de-black mb-1">Cohort</label>
            <select value={selected} onChange={e => setSelected(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
              <option value="">— Select cohort —</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name} ({c.level})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-de-black mb-1">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"/>
          </div>
        </div>
      </Card>

      {selected && date && (
        <>
          {/* Quick actions */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-2 flex-wrap">
              {Object.entries(STATUS_CONFIG).map(([s, c]) => (
                <button key={s} onClick={() => markAll(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors ${c.bg} ${c.text} border-transparent hover:ring-2 ${c.ring}`}>
                  {c.icon} Mark All {c.label}
                </button>
              ))}
            </div>
            <Button variant="primary" loading={saving} onClick={handleSave}>💾 Save Attendance</Button>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(summary).map(([k, v]) => (
              <div key={k} className={`${STATUS_CONFIG[k].bg} rounded-xl p-3 text-center`}>
                <p className={`text-xl font-bold ${STATUS_CONFIG[k].text}`}>{v}</p>
                <p className={`text-xs font-semibold ${STATUS_CONFIG[k].text} mt-0.5`}>{STATUS_CONFIG[k].label}</p>
              </div>
            ))}
          </div>

          {/* Student list */}
          {loading ? (
            <div className="flex justify-center py-10"><Spinner className="text-brand-500"/></div>
          ) : records.length === 0 ? (
            <Card className="p-8 text-center text-gray-400 text-sm">No students in this cohort yet.</Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="divide-y divide-gray-100">
                {records.map(r => (
                  <div key={r.student_id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-500 text-white flex items-center justify-center text-xs font-bold">
                        {r.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-de-black">{r.name}</p>
                        {r.student_number && <p className="text-xs text-gray-400 font-mono">{r.student_number}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      {Object.entries(STATUS_CONFIG).map(([s, c]) => (
                        <button key={s} onClick={() => setStatus(r.student_id, s)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border-2
                            ${r.status === s
                              ? `${c.bg} ${c.text} border-current ring-2 ${c.ring} ring-offset-1`
                              : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`}>
                          {c.icon}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 bg-surface border-t border-gray-200 flex justify-end">
                <Button variant="primary" loading={saving} onClick={handleSave}>💾 Save Attendance</Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
